import { useShallow } from 'zustand/react/shallow';
import { FolderTree } from 'lucide-react';
import { SideNavigationLinks } from '@/components/side-navigation/side-navigation-links';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { useBatchStore } from '@/hooks/data/batch/useBatchStore';
import { useDamNavigation } from '@/hooks/useDamNavigation';
import { defaultPaginationSize } from '@/types/pagination';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { TaskState } from '@/types/uploads';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

/**
 * The Asset Management nav link, split out of SideNavigation because it is the
 * only consumer of the two progress stores. Both write constantly — uploads once
 * per file chunk, batches once a second per running batch — and `totalProgress`
 * genuinely changes on every write, so this cannot be isolated further. Keeping
 * the subscription here means the tick re-renders one Button instead of the whole
 * navigation (avatar, dropdown, every other link).
 *
 * Behaviour is unchanged from the previous inline version in side-navigation.tsx.
 */
export const AssetManagementNavLink = ({ collapsed }: { collapsed: boolean }) => {
  const { navigateAndReset } = useDamNavigation();
  const {
    query: { pageIndex, pageSize },
  } = useRouter();
  const { isAssetManagementSection, isManagePage } = useCurrentPage();
  const tNav = useTranslations('menu.nav');

  const uploads = useUploadStore(
    useShallow((state) => ({
      count: Object.values(state.uploads).filter((session) => session.uploadState !== TaskState.NotStarted).length,
      totalProgress: state.totalProgress,
    })),
  );
  const batches = useBatchStore(
    useShallow((state) => ({ count: state.batches.length, totalProgress: state.totalProgress })),
  );

  const totalCount = uploads.count + batches.count;
  const taskProgress =
    isManagePage && totalCount > 0
      ? (uploads.totalProgress * uploads.count + batches.totalProgress * batches.count) / totalCount
      : 0;

  return (
    <SideNavigationLinks
      collapsed={collapsed}
      label={tNav('assetManagement')}
      icon={FolderTree}
      selected={isAssetManagementSection}
      className="text-text-base"
      progress={taskProgress}
      onClick={() => {
        void navigateAndReset('/browse', {
          pageIndex: 1,
          pageSize: pageSize && pageIndex ? pageSize : defaultPaginationSize,
        });
      }}
    />
  );
};
